import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PERMISSIONS, type InvoiceKind, type SessionUser } from '@qlkho-v3/shared';

import { api, queryString } from './api';
import { AsyncSelect, type OptionItem } from './components';
import { DateRangeFilter } from './date-range-filter';
import { DELIVERY_STATUS_OPTIONS, deliveryStatusLabel, deliveryStatusTone } from './delivery-status';
import { DeliveryStatusModal, type DeliveryInvoice } from './delivery-status-modal';
import { downloadExcelWorkbook, createExcelWorkbook } from './excel-export';
import { ArrowDownIcon, ArrowUpIcon, ChartIcon, ChevronRightIcon, DebtIcon, DeliveryIcon, EditIcon, ExcelIcon, EyeIcon, FilterIcon, LockIcon, MoreIcon, PlusIcon, PrintIcon, ReloadIcon, ReturnIcon, SearchIcon, TrashIcon, UnlockIcon } from './icons';
import { InboundDeleteModal, type DeletableInboundInvoice } from './inbound-delete-modal';
import { InboundPaymentModal, type InboundDebtInvoice } from './inbound-payment-modal';
import { InboundReturnModal } from './inbound-return-modal';
import { InvoiceDetailModal } from './invoice-detail-modal';
import { InvoiceEditor } from './invoice-editor';
import type { CrmPrefill } from './crm-prefill';
import { InvoiceLockModal, type LockableInboundInvoice } from './invoice-lock-modal';
import { OutboundDeleteModal, type DeletableOutboundInvoice } from './outbound-delete-modal';
import { OutboundPaymentModal, type OutboundDebtInvoice } from './outbound-payment-modal';
import { OutboundProductsModal } from './outbound-products-modal';
import { OutboundReturnModal } from './outbound-return-modal';

interface InvoiceRow {
  id: string;
  sku: string;
  date: string;
  deliveryDate?: string | null;
  total: number;
  profit?: number;
  paid: number;
  paidDebt: number;
  refund: number;
  remaining: number;
  partnerName: string;
  creatorName: string;
  isLock: number;
  deliveryStatus?: number;
  updatedAt: string;
}

interface ListResponse {
  items: InvoiceRow[];
  nextCursor: string | null;
  summary?: {
    totalCount: number;
    totalAmount: number;
    totalCollected: number;
    totalDebt: number;
    totalRefund: number;
    totalProfit: number;
  };
}

type PaymentStatus = '' | 'unpaid' | 'partial' | 'paid' | 'refund';
type DeliveryStatus = '' | 'waiting' | '1' | '3' | '4' | '5';

function dateOffset(days: number): string {
  return new Date(Date.now() + 7 * 60 * 60 * 1000 + days * 86_400_000).toISOString().slice(0, 10);
}

function money(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
}

function displayDate(value: string): { date: string; time: string } {
  const [date, time = ''] = value.split(' ');
  const [year, month, day] = (date ?? '').split('-');
  return { date: [day, month, year].filter(Boolean).join('/'), time: time.slice(0, 5) };
}

export function InvoiceCode({ sku, creatorName, date }: Pick<InvoiceRow, 'sku' | 'creatorName' | 'date'>) {
  const when = displayDate(date);
  return (
    <div className="invoice-code-layout">
      <div className="invoice-code-main"><strong>{sku}</strong><span title={creatorName || ''}>{creatorName || '—'}</span></div>
      <div className="invoice-document-date" title="Ngày chứng từ"><strong>{when.date || '—'}</strong><span>{when.time}</span></div>
    </div>
  );
}

function can(user: SessionUser, permission: string): boolean {
  return user.roles.includes('Super Admin') || user.permissions.includes(permission);
}

export function InvoiceList({
  user,
  kind,
  notify,
  prefill,
  onPrefillUsed
}: {
  user: SessionUser;
  kind: InvoiceKind;
  notify: (message: string) => void;
  prefill?: CrmPrefill | null;
  onPrefillUsed?: () => void;
}) {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState(dateOffset(-30));
  const [to, setTo] = useState(dateOffset(0));
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('');
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus>('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [partnerId, setPartnerId] = useState<number>();
  const [partnerLabel, setPartnerLabel] = useState('');
  const [creatorId, setCreatorId] = useState<number>();
  const [creatorLabel, setCreatorLabel] = useState('');
  const [discountTypeId, setDiscountTypeId] = useState<number>();
  const [discountTypeLabel, setDiscountTypeLabel] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const emptySummary = { totalCount: 0, totalAmount: 0, totalCollected: 0, totalDebt: 0, totalRefund: 0, totalProfit: 0 };
  const [summary, setSummary] = useState<NonNullable<ListResponse['summary']>>(emptySummary);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState<{ id?: string; nonce: number; prefill?: CrmPrefill } | null>(null);
  // BUOC523: CRM gui don nhap sang -> mo form them hoa don xuat da dien san
  useEffect(() => {
    if (!prefill || kind !== 'outbound') return;
    setEditor({ nonce: Date.now(), prefill });
    onPrefillUsed?.();
  }, [prefill, kind, onPrefillUsed]);
  const [detail, setDetail] = useState<{ id: string; autoPrint?: boolean } | null>(null);
  const [debtInvoice, setDebtInvoice] = useState<InboundDebtInvoice | null>(null);
  const [outboundDebtInvoice, setOutboundDebtInvoice] = useState<OutboundDebtInvoice | null>(null);
  const [lockInvoice, setLockInvoice] = useState<LockableInboundInvoice | null>(null);
  const [deleteInvoice, setDeleteInvoice] = useState<DeletableInboundInvoice | null>(null);
  const [outboundDeleteInvoice, setOutboundDeleteInvoice] = useState<DeletableOutboundInvoice | null>(null);
  const [returnInvoiceId, setReturnInvoiceId] = useState<string | null>(null);
  const [deliveryInvoice, setDeliveryInvoice] = useState<DeliveryInvoice | null>(null);
  const [showProductsSold, setShowProductsSold] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuOpensUp, setMenuOpensUp] = useState(false);
  const requestSequence = useRef(0);
  const outbound = kind === 'outbound';
  const canCreate = outbound || can(user, PERMISSIONS.inboundCreate);
  const canUpdate = outbound || can(user, PERMISSIONS.inboundUpdate);
  const canViewDetail = outbound || can(user, PERMISSIONS.inboundDetail);
  const canPayDebt = outbound || can(user, PERMISSIONS.inboundPay);
  const canLock = outbound || can(user, PERMISSIONS.inboundLock);
  const canDelete = outbound || can(user, PERMISSIONS.inboundDelete);
  const canReturn = outbound || can(user, PERMISSIONS.inboundUpdate);

  const filters = useMemo(() => ({
    q,
    from,
    to,
    paymentStatus: paymentStatus || undefined,
    deliveryStatus: outbound && deliveryStatus !== '' ? deliveryStatus : undefined,
    partnerId,
    creatorId,
    discountTypeId,
    sortBy,
    sortDir,
    limit: 500
  }), [creatorId, deliveryStatus, discountTypeId, from, outbound, partnerId, paymentStatus, q, sortBy, sortDir, to]);

  const load = useCallback(async () => {
    const currentRequest = ++requestSequence.current;
    if (!from || !to || from > to) {
      setRows([]);
      setSummary(emptySummary);
      setLoading(false);
      setLoadingMore(false);
      setError('Khoảng ngày không hợp lệ: ngày bắt đầu phải trước hoặc bằng ngày kết thúc.');
      return;
    }
    let pageCursor: string | null = null;
    let collected: InvoiceRow[] = [];
    const seenIds = new Set<string>();
    const seenCursors = new Set<string>();
    let expectedTotal: number | undefined;
    let page = 0;
    setRows([]);
    setSummary(emptySummary);
    setLoading(true);
    setLoadingMore(false);
    setError('');
    try {
      do {
        const result: ListResponse = await api<ListResponse>(
          `/api/invoices/${kind}?${queryString({ ...filters, cursor: pageCursor ?? undefined })}`
        );
        if (currentRequest !== requestSequence.current) return;
        for (const item of result.items) {
          if (!seenIds.has(item.id)) {
            seenIds.add(item.id);
            collected.push(item);
          }
        }
        pageCursor = result.nextCursor;
        if (pageCursor && seenCursors.has(pageCursor)) throw new Error('Phân trang trả về dữ liệu lặp. Đã dừng tải để tránh trùng hóa đơn.');
        if (pageCursor) seenCursors.add(pageCursor);
        page += 1;
        if (page === 1 || page % 5 === 0 || !pageCursor) setRows([...collected]);
        if (result.summary) {
          expectedTotal = Number(result.summary.totalCount);
          setSummary(result.summary);
        }
        setLoading(false);
        setLoadingMore(Boolean(pageCursor));
        if (pageCursor && page % 5 === 0) await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      } while (pageCursor);
      if (expectedTotal !== undefined && collected.length !== expectedTotal) {
        throw new Error(`Dữ liệu tải chưa đủ (${collected.length}/${expectedTotal}) hoặc có dòng bị trùng.`);
      }
    } catch (reason) {
      if (currentRequest === requestSequence.current) setError(reason instanceof Error ? reason.message : 'Không thể tải danh sách.');
    } finally {
      if (currentRequest === requestSequence.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [filters, from, kind, to]);

  function setOption(
    item: OptionItem | null,
    setId: (value: number | undefined) => void,
    setLabel: (value: string) => void
  ) {
    setId(item ? Number(item.id) : undefined);
    setLabel(String(item?.label ?? ''));
  }

  function resetAdvancedFilters() {
    setPartnerId(undefined);
    setPartnerLabel('');
    setCreatorId(undefined);
    setCreatorLabel('');
    setDiscountTypeId(undefined);
    setDiscountTypeLabel('');
    setSortBy('date');
    setSortDir('desc');
  }

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), q ? 280 : 0);
    return () => window.clearTimeout(timer);
  }, [load, q]);

  useEffect(() => {
    setOpenMenuId(null);
  }, [kind, filters]);

  useEffect(() => {
    const closeOnOutside = (event: PointerEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest('.row-action-menu')) {
        setOpenMenuId(null);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenMenuId(null);
    };
    document.addEventListener('pointerdown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const loadingData = loading || loadingMore;
  const progress = summary.totalCount > 0
    ? Math.min(100, Math.round((rows.length / Number(summary.totalCount)) * 100))
    : loadingData ? 0 : 100;

  function exportExcel() {
    const headers = [
      'Mã hóa đơn',
      outbound ? 'Khách hàng' : 'Nhà phân phối',
      ...(outbound ? ['Ngày chứng từ', 'Ngày giao dự kiến'] : ['Ngày chứng từ']),
      'Người tạo',
      'Tổng tiền',
      ...(outbound ? ['Lợi nhuận'] : []),
      'Đã trả',
      'Còn lại',
      ...(outbound ? ['Cần hoàn'] : []),
      'Trạng thái thanh toán',
      ...(outbound ? ['Trạng thái đơn hàng'] : []),
      'Đã khóa'
    ];
    const exportRows = rows.map((row) => {
      const when = displayDate(row.date);
      const paid = Number(row.paid) + Number(row.paidDebt);
      return [
        row.sku,
        row.partnerName || 'Không xác định',
        ...(outbound ? [
          `${when.date}${when.time ? ` ${when.time}` : ''}`,
          row.deliveryDate ? displayDate(row.deliveryDate).date : ''
        ] : [`${when.date}${when.time ? ` ${when.time}` : ''}`]),
        row.creatorName || '',
        Number(row.total),
        ...(outbound ? [Number(row.profit ?? 0)] : []),
        paid,
        Number(row.remaining),
        ...(outbound ? [Number(row.refund ?? 0)] : []),
        Number(row.refund ?? 0) > 0 ? 'Chờ hoàn tiền' : row.remaining <= 0 ? 'Đã trả đủ' : paid > 0 ? 'Đã trả một phần' : 'Chưa trả',
        ...(outbound ? [deliveryStatusLabel(row.deliveryStatus)] : []),
        row.isLock ? 'Có' : 'Không'
      ];
    });
    const filename = `hoa-don-${outbound ? 'xuat' : 'nhap'}-${from}-${to}.xlsx`;
    downloadExcelWorkbook(createExcelWorkbook(`Hóa đơn ${outbound ? 'xuất' : 'nhập'}`, headers, exportRows), filename);
    notify(`Đã xuất ${money(rows.length)} hóa đơn ra Excel.`);
  }

  return (
    <>
      <div className="invoice-screen">
        <div className="workspace-heading">
          <h1>Hóa đơn {outbound ? 'xuất' : 'nhập'}</h1>
          {canCreate ? <button className="primary-action" onClick={() => setEditor({ nonce: Date.now() })}><PlusIcon /> Thêm hóa đơn {outbound ? 'xuất' : 'nhập'}</button> : null}
        </div>
        <section className="invoice-workspace">
          <div className="filter-bar">
            <div className="search-box"><SearchIcon /><input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Mã hóa đơn, đối tác, người tạo" aria-label="Tìm hóa đơn" />{q ? <button onClick={() => setQ('')}>Xóa</button> : null}</div>
            <DateRangeFilter from={from} to={to} onChange={(nextFrom, nextTo) => { setFrom(nextFrom); setTo(nextTo); }} />
            {outbound ? (
              <select className="list-filter-select order-status-filter" aria-label="Trạng thái đơn hàng" value={deliveryStatus} onChange={(event) => setDeliveryStatus(event.target.value as DeliveryStatus)}>
                <option value="">Đơn hàng: Tất cả</option>
                {DELIVERY_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value === 0 ? 'waiting' : String(option.value)}>{option.label}</option>)}
              </select>
            ) : null}
            <select className="list-filter-select payment-status-filter" aria-label="Trạng thái thanh toán" value={paymentStatus} onChange={(event) => setPaymentStatus(event.target.value as PaymentStatus)}>
              <option value="">Thanh toán: Tất cả</option>
              <option value="unpaid">Chưa trả</option>
              <option value="partial">Đã trả một phần</option>
              <option value="paid">Đã trả đủ</option>
              {outbound ? <option value="refund">Chờ hoàn tiền</option> : null}
            </select>
            <div className="filter-actions">
              <button type="button" className={`filter-icon-button ${showAdvanced ? 'active' : ''}`} title="Bộ lọc nâng cao" aria-label="Bộ lọc nâng cao" aria-expanded={showAdvanced} onClick={() => setShowAdvanced((current) => !current)}><FilterIcon /></button>
              {outbound ? <button type="button" className="filter-icon-button" title="Thống kê sản phẩm đã bán" aria-label="Thống kê sản phẩm đã bán" onClick={() => setShowProductsSold(true)}><ChartIcon /></button> : null}
              <button type="button" className="filter-icon-button excel-action" title={loadingData ? 'Đợi tải đủ dữ liệu để xuất Excel' : 'Xuất danh sách ra Excel'} aria-label="Xuất danh sách ra Excel" disabled={loadingData || rows.length === 0} onClick={exportExcel}><ExcelIcon /></button>
              <button type="button" className="filter-icon-button" title="Tải lại dữ liệu" aria-label="Tải lại dữ liệu" onClick={() => void load()}><ReloadIcon className={loadingData ? 'is-spinning' : ''} /></button>
            </div>
          </div>

          {showAdvanced ? <div className="advanced-filter-bar">
            <AsyncSelect label={outbound ? 'Khách hàng' : 'Nhà phân phối'} path={`/api/options/${outbound ? 'customers' : 'distributors'}`} value={partnerId} selectedLabel={partnerLabel} placeholder="Tìm theo tên" minimumQueryLength={2} pageSize={30} onChange={(item) => setOption(item, setPartnerId, setPartnerLabel)} />
            <AsyncSelect label="Người tạo" path="/api/options/invoice-creators" value={creatorId} selectedLabel={creatorLabel} placeholder="Tìm người tạo" minimumQueryLength={1} pageSize={30} onChange={(item) => setOption(item, setCreatorId, setCreatorLabel)} />
            <AsyncSelect label="Loại CK hóa đơn" path="/api/options/discount-types" value={discountTypeId} selectedLabel={discountTypeLabel} placeholder="Tất cả" pageSize={30} onChange={(item) => setOption(item, setDiscountTypeId, setDiscountTypeLabel)} />
            <div className="field sort-filter"><label htmlFor={`sort-${kind}`}>Sắp xếp</label><div><select id={`sort-${kind}`} value={sortBy} onChange={(event) => setSortBy(event.target.value)}><option value="date">Ngày chứng từ</option><option value="sku">Mã chứng từ</option><option value="partner">{outbound ? 'Khách hàng' : 'Nhà phân phối'}</option><option value="creator">Người tạo</option><option value="total">Tổng tiền</option>{outbound ? <option value="profit">Lợi nhuận</option> : null}<option value="paid">Đã trả</option><option value="remaining">Còn lại</option></select><button type="button" className="filter-icon-button" title={sortDir === 'desc' ? 'Giảm dần' : 'Tăng dần'} aria-label={sortDir === 'desc' ? 'Giảm dần' : 'Tăng dần'} onClick={() => setSortDir((current) => current === 'desc' ? 'asc' : 'desc')}>{sortDir === 'desc' ? <ArrowDownIcon /> : <ArrowUpIcon />}</button></div></div>
            <button type="button" className="secondary-action reset-filter" onClick={resetAdvancedFilters}>Đặt lại</button>
          </div> : null}

          <section className="summary-strip" aria-label="Tổng quan danh sách">
            <div><span>Chứng từ</span><strong>{money(summary.totalCount)}</strong></div>
            <div><span>Tổng tiền</span><strong>{money(summary.totalAmount)} ₫</strong></div>
            <div><span>Đã thanh toán</span><strong>{money(summary.totalCollected)} ₫</strong></div>
            <div className={summary.totalDebt > 0 ? 'attention' : ''}><span>Còn lại</span><strong>{money(summary.totalDebt)} ₫</strong></div>
            {outbound ? <div><span>Lợi nhuận</span><strong>{money(summary.totalProfit)} ₫</strong></div> : null}
            {outbound && summary.totalRefund > 0 ? <div className="attention"><span>Cần hoàn</span><strong>{money(summary.totalRefund)} ₫</strong></div> : null}
          </section>

          <div className="invoice-table-wrap">
            <table className={`invoice-table ${outbound ? 'outbound-invoice-table' : 'inbound-invoice-table'}`}>
            <thead><tr><th>Chứng từ</th><th>{outbound ? 'Khách hàng' : 'Nhà phân phối'}</th>{outbound ? <th>Ngày giao dự kiến</th> : null}<th className="align-right">Tổng tiền</th>{outbound ? <th className="align-right">Lợi nhuận</th> : null}<th className="align-right">Đã trả</th><th className="align-right">Còn lại</th><th>Trạng thái</th><th /></tr></thead>
            <tbody>
              {loading ? Array.from({ length: 8 }, (_, index) => <tr className="skeleton-row" key={index}>{Array.from({ length: outbound ? 8 : 6 }, (_, cell) => <td key={cell}><span /></td>)}<td /></tr>) : null}
              {!loading && rows.length === 0 ? <tr><td colSpan={outbound ? 9 : 7}><div className="empty-list"><SearchIcon /><strong>Không có chứng từ</strong></div></td></tr> : null}
              {!loading ? rows.map((row) => {
                const delivery = outbound && row.deliveryDate ? displayDate(row.deliveryDate) : null;
                const paid = Number(row.paid) + Number(row.paidDebt);
                const hasRefund = Number(row.refund ?? 0) > 0;
                const paymentTone = hasRefund ? 'refund' : row.remaining <= 0 ? 'done' : paid > 0 ? 'partial' : 'unpaid';
                return (
                  <tr key={row.id} onDoubleClick={() => canViewDetail ? setDetail({ id: row.id }) : canUpdate && !row.isLock ? setEditor({ id: row.id, nonce: Date.now() }) : undefined}>
                    <td className="invoice-code"><InvoiceCode sku={row.sku} creatorName={row.creatorName} date={row.date} /></td>
                    <td className="partner-cell"><strong>{row.partnerName || 'Không xác định'}</strong></td>
                    {outbound ? <td className="date-cell">{delivery ? <strong>{delivery.date}</strong> : <span title="Chưa có ngày giao">—</span>}</td> : null}
                    <td className="align-right amount-cell">{money(row.total)} ₫</td>
                    {outbound ? <td className={`align-right profit-cell ${Number(row.profit ?? 0) < 0 ? 'is-negative' : ''}`}>{money(Number(row.profit ?? 0))} ₫</td> : null}
                    <td className="align-right muted-amount">{money(paid)} ₫</td>
                    <td className={`align-right debt-cell ${row.remaining > 0 ? 'has-debt' : ''}`}>{money(row.remaining)} ₫</td>
                    <td><div className="status-stack"><span className={`payment-status ${paymentTone}`}>{hasRefund ? `Hoàn ${money(row.refund)} ₫` : row.remaining <= 0 ? 'Đã trả đủ' : paid > 0 ? 'Một phần' : 'Chưa trả'}</span>{outbound ? <span className={`delivery delivery-${deliveryStatusTone(row.deliveryStatus)}`}>{deliveryStatusLabel(row.deliveryStatus)}</span> : null}{row.isLock ? <span className="locked"><LockIcon /> Đã khóa</span> : null}</div></td>
                    <td><div className="invoice-actions" onDoubleClick={(event) => event.stopPropagation()}>
                      {canViewDetail || canPayDebt || canUpdate || canReturn || canLock || canDelete ? (
                        <details className={`row-action-menu ${openMenuId === row.id && menuOpensUp ? 'open-up' : ''}`} open={openMenuId === row.id}>
                          <summary title="Thao tác hóa đơn" aria-label={`Thao tác ${row.sku}`} onClick={(event) => {
                            event.preventDefault();
                            const table = event.currentTarget.closest('.invoice-table-wrap');
                            const bottom = table?.getBoundingClientRect().bottom ?? window.innerHeight;
                            setMenuOpensUp(bottom - event.currentTarget.getBoundingClientRect().top < (outbound ? 240 : 185));
                            setOpenMenuId((current) => current === row.id ? null : row.id);
                          }}><MoreIcon /></summary>
                          <div className="row-action-popover" onClickCapture={(event) => { if ((event.target as Element).closest('button:not(:disabled)')) setOpenMenuId(null); }}>
                            {canViewDetail ? <button type="button" onClick={() => setDetail({ id: row.id })}><EyeIcon /> Xem chi tiết</button> : null}
                            {canPayDebt ? <button type="button" disabled={outbound ? row.remaining <= 0 && !hasRefund : row.remaining <= 0} onClick={() => outbound ? setOutboundDebtInvoice({ id: row.id, sku: row.sku, partnerName: row.partnerName || 'Không xác định', remaining: Number(row.remaining), refund: Number(row.refund ?? 0) }) : setDebtInvoice({ id: row.id, sku: row.sku, partnerName: row.partnerName || 'Không xác định', remaining: Number(row.remaining) })}><DebtIcon /> {outbound ? hasRefund ? 'Hoàn tiền' : 'Thu nợ' : 'Trả nợ'}</button> : null}
                            {canUpdate ? <button type="button" disabled={Boolean(row.isLock)} onClick={() => setEditor({ id: row.id, nonce: Date.now() })}><EditIcon /> Cập nhật</button> : null}
                            {outbound ? <><button type="button" onClick={() => setDeliveryInvoice({ id: row.id, sku: row.sku, deliveryStatus: Number(row.deliveryStatus ?? 0), updatedAt: row.updatedAt })}><DeliveryIcon /> Trạng thái giao hàng</button><button type="button" onClick={() => setDetail({ id: row.id, autoPrint: true })}><PrintIcon /> In hóa đơn</button></> : null}
                            {canReturn ? <button type="button" disabled={Boolean(row.isLock)} onClick={() => setReturnInvoiceId(row.id)}><ReturnIcon /> {outbound ? 'Khách trả hàng' : 'Trả hàng nhà phân phối'}</button> : null}
                            {canLock ? <button type="button" onClick={() => setLockInvoice({ id: row.id, sku: row.sku, isLocked: Boolean(row.isLock) })}>{row.isLock ? <UnlockIcon /> : <LockIcon />} {row.isLock ? 'Mở khóa' : 'Khóa'}</button> : null}
                            {canDelete ? <button type="button" className="danger-menu-item" disabled={Boolean(row.isLock) || paid > 0} onClick={() => outbound ? setOutboundDeleteInvoice({ id: row.id, sku: row.sku, partnerName: row.partnerName || 'Không xác định', updatedAt: row.updatedAt }) : setDeleteInvoice({ id: row.id, sku: row.sku, partnerName: row.partnerName || 'Không xác định', updatedAt: row.updatedAt })}><TrashIcon /> Xóa hóa đơn</button> : null}
                          </div>
                        </details>
                      ) : <ChevronRightIcon className="chevron" />}
                    </div></td>
                  </tr>
                );
              }) : null}
            </tbody>
            </table>
          </div>
          <footer className={`data-status ${error ? 'error' : loadingData ? 'loading' : 'ready'}`} role="status" aria-live="polite">
            <span className="data-progress" style={{ width: `${progress}%` }} />
            <span className="status-indicator">{loadingData ? <span className="mini-spinner" /> : null}</span>
            <span>
              {error
                ? 'Tải dữ liệu bị gián đoạn'
                : loading
                  ? 'Đang tải dữ liệu…'
                  : loadingMore
                    ? `Đang tải dữ liệu: ${money(rows.length)} / ${money(summary.totalCount)} dòng`
                    : `Đã tải đủ ${money(rows.length)} dòng`}
            </span>
            {!loading && !error ? <em>{progress}%</em> : null}
            {error ? <button type="button" onClick={() => void load()}>Tải lại</button> : null}
          </footer>
        </section>
      </div>

      {editor ? <InvoiceEditor key={editor.nonce} kind={kind} invoiceId={editor.id} prefill={editor.prefill} onClose={() => setEditor(null)} onSaved={(message) => { setEditor(null); notify(message); void load(); }} /> : null}
      {detail ? <InvoiceDetailModal kind={kind} invoiceId={detail.id} autoPrint={detail.autoPrint} canPay={canPayDebt} canReturn={canReturn} onClose={() => setDetail(null)} onPay={(invoice) => { setDetail(null); if (outbound) setOutboundDebtInvoice(invoice); else setDebtInvoice(invoice); }} onReturn={(invoiceId) => { setDetail(null); setReturnInvoiceId(invoiceId); }} /> : null}
      {debtInvoice ? <InboundPaymentModal invoice={debtInvoice} onClose={() => setDebtInvoice(null)} onPaid={(message) => { setDebtInvoice(null); notify(message); void load(); }} /> : null}
      {outboundDebtInvoice ? <OutboundPaymentModal invoice={outboundDebtInvoice} onClose={() => setOutboundDebtInvoice(null)} onPaid={(message) => { setOutboundDebtInvoice(null); notify(message); void load(); }} /> : null}
      {lockInvoice ? <InvoiceLockModal kind={kind} invoice={lockInvoice} username={user.username} onClose={() => setLockInvoice(null)} onChanged={(message) => { setLockInvoice(null); notify(message); void load(); }} /> : null}
      {deleteInvoice ? <InboundDeleteModal invoice={deleteInvoice} onClose={() => setDeleteInvoice(null)} onDeleted={(message) => { setDeleteInvoice(null); notify(message); void load(); }} /> : null}
      {outboundDeleteInvoice ? <OutboundDeleteModal invoice={outboundDeleteInvoice} onClose={() => setOutboundDeleteInvoice(null)} onDeleted={(message) => { setOutboundDeleteInvoice(null); notify(message); void load(); }} /> : null}
      {returnInvoiceId && outbound ? <OutboundReturnModal invoiceId={returnInvoiceId} onClose={() => setReturnInvoiceId(null)} onReturned={(message) => { setReturnInvoiceId(null); notify(message); void load(); }} /> : null}
      {returnInvoiceId && !outbound ? <InboundReturnModal invoiceId={returnInvoiceId} onClose={() => setReturnInvoiceId(null)} onReturned={(message) => { setReturnInvoiceId(null); notify(message); void load(); }} /> : null}
      {deliveryInvoice ? <DeliveryStatusModal invoice={deliveryInvoice} onClose={() => setDeliveryInvoice(null)} onChanged={(message) => { setDeliveryInvoice(null); notify(message); void load(); }} /> : null}
      {showProductsSold ? <OutboundProductsModal from={from} to={to} onClose={() => setShowProductsSold(false)} /> : null}
    </>
  );
}
